import React from 'react';
import styled from 'styled-components';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip
} from 'recharts';

// Helper to determine sentiment color
function getSentimentColor(score) {
  if (score > 0.3) return '#10b981'; // Green - positive
  if (score < -0.3) return '#ef4444'; // Red - negative
  return '#f59e0b'; // Orange - neutral
}

function SentimentOverviewCard({ sentimentData }) {
  if (!sentimentData || !sentimentData.overview) {
    return null;
  }

  const { overview } = sentimentData;

  const chartData = [
    { name: 'Positive', value: overview.breakdown.positive, color: '#10b981' },
    { name: 'Neutral', value: overview.breakdown.neutral, color: '#f59e0b' },
    { name: 'Negative', value: overview.breakdown.negative, color: '#ef4444' }
  ].filter((item) => item.value > 0); // Only show segments with data

  return (
    <Container>
      <Title>Sentiment Analysis Overview</Title>

      <Content>
        <StatsSection>
          <StatCard>
            <StatLabel>Overall Sentiment</StatLabel>
            <StatValue $sentiment={getSentimentColor(overview.averageScore)}>
              {overview.averageScore > 0 ? '+' : ''}
              {overview.averageScore}
            </StatValue>
            <StatHint>Average score (-1 to +1)</StatHint>
          </StatCard>

          <StatCard>
            <StatLabel>Comments Analyzed</StatLabel>
            <StatValue>
              {overview.totalAnalysed} / {overview.totalComments}
            </StatValue>
            <StatHint>
              {Math.round(
                (overview.totalAnalysed / overview.totalComments) * 100
              )}
              % coverage
            </StatHint>
          </StatCard>
        </StatsSection>

        <ChartSection>
          <ChartTitle>Sentiment Breakdown</ChartTitle>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => <LegendText>{value}</LegendText>}
              />
            </PieChart>
          </ResponsiveContainer>

          <BreakdownStats>
            <BreakdownItem>
              <ColorDot $color="#10b981" />
              <span>
                <strong>Positive:</strong> {overview.breakdown.positive} (
                {overview.breakdown.positivePercent}%)
              </span>
            </BreakdownItem>
            <BreakdownItem>
              <ColorDot $color="#f59e0b" />
              <span>
                <strong>Neutral:</strong> {overview.breakdown.neutral} (
                {overview.breakdown.neutralPercent}%)
              </span>
            </BreakdownItem>
            <BreakdownItem>
              <ColorDot $color="#ef4444" />
              <span>
                <strong>Negative:</strong> {overview.breakdown.negative} (
                {overview.breakdown.negativePercent}%)
              </span>
            </BreakdownItem>
          </BreakdownStats>
        </ChartSection>
      </Content>

      {sentimentData.topEmotions && sentimentData.topEmotions.length > 0 && (
        <EmotionsSection>
          <EmotionsTitle>Top Emotions Detected:</EmotionsTitle>
          <EmotionsList>
            {sentimentData.topEmotions.slice(0, 5).map((emotion) => (
              <EmotionTag key={emotion.emotion}>
                {emotion.emotion} ({emotion.count})
              </EmotionTag>
            ))}
          </EmotionsList>
        </EmotionsSection>
      )}
    </Container>
  );
}

// Custom label for pie chart
function renderCustomLabel({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent
}) {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize="14"
      fontWeight="600">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

// Custom tooltip
function CustomTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    return (
      <TooltipContainer>
        <TooltipLabel>{payload[0].name}</TooltipLabel>
        <TooltipValue>{payload[0].value} comments</TooltipValue>
      </TooltipContainer>
    );
  }
  return null;
}

const Container = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 2rem;
  padding: 1.5rem;
`;

const Title = styled.h3`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
`;

const Content = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const StatsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StatCard = styled.div`
  background: #f9fafb;
  border-radius: 6px;
  padding: 1rem;
`;

const StatLabel = styled.div`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  color: ${(props) => props.$sentiment || 'var(--brown)'};
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.25rem;
`;

const StatHint = styled.div`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
`;

const ChartSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const ChartTitle = styled.h4`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
`;

const BreakdownStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const BreakdownItem = styled.div`
  align-items: center;
  color: var(--light-brown);
  display: flex;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  gap: 0.5rem;
`;

const ColorDot = styled.div`
  background: ${(props) => props.$color};
  border-radius: 50%;
  flex-shrink: 0;
  height: 12px;
  width: 12px;
`;

const EmotionsSection = styled.div`
  border-top: 1px solid #e5e7eb;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
`;

const EmotionsTitle = styled.h4`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
`;

const EmotionsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const EmotionTag = styled.span`
  background: #ede9fe;
  border-radius: 4px;
  color: #6b21a8;
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
`;

const LegendText = styled.span`
  color: var(--brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
`;

const TooltipContainer = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
`;

const TooltipLabel = styled.div`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const TooltipValue = styled.div`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
`;

export default SentimentOverviewCard;
